import { useCallback, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Laptop, Check, AlertTriangle, Loader2, Code2, Sparkles } from 'lucide-react'
import { api } from '@/services/api'

/**
 * Approving a device that asked to be connected.
 *
 * The machine already said what it is, and this browser already knows who the
 * user is, so the only thing left is agreeing. Nothing is typed here on
 * purpose: a code copied between two screens is a step that can go wrong, and
 * neither end needed the person to supply it.
 *
 * The last thing this page does is hand control back to whatever sent the
 * person here. That used to be unconditional: approving always ended by
 * sending the browser to `nevtan://`, which raises the desktop app. Correct
 * when the desktop app is what asked, and wrong for every other client --
 * someone who signed in from VS Code was told "Opening NevTan AI…" and offered
 * a button that would launch a different application than the one they were
 * working in.
 */

/** The apps that can be handed control at the end. */
type LaunchTarget = 'vscode' | 'desktop'

/** How each one reads, wherever it is named. */
const APPS: Record<LaunchTarget, { name: string; scheme: string }> = {
  // The editor's own URI handler. Opening this focuses the running editor and
  // wakes this extension inside it; the operating system starts one only if
  // none is running, so approving twice never leaves two editors open.
  vscode: { name: 'VS Code', scheme: 'vscode://nevtan.nevtan-ai-vscode/connected' },
  // Nothing is passed in the URL: it exists only to raise the window, so a
  // link that opens the app cannot make it do anything.
  desktop: { name: 'NevTan AI', scheme: 'nevtan://connected' },
}

/** Where a remembered choice is kept. Per browser, which is where the choice was made. */
const PREFERENCE_KEY = 'nevtan.launchPreference'

/**
 * How long to wait before deciding the app did not open.
 *
 * <p>A browser is never told whether a scheme handler did anything -- an
 * unregistered scheme and a successful launch look identical from here. The
 * one signal available is focus: an app that opened took it. So this waits,
 * and treats "still looking at this page" as the failure it almost always is.
 */
const FALLBACK_MS = 2500

/** The extension a `return` URI is allowed to address, and nothing else. */
const EXTENSION_ID = 'nevtan.nevtan-ai-vscode'

function readPreference(): LaunchTarget | null {
  try {
    const saved = window.localStorage.getItem(PREFERENCE_KEY)
    return saved === 'vscode' || saved === 'desktop' ? saved : null
  } catch {
    // Storage can be denied outright in a private window. Not remembering is
    // a worse experience, not a broken one.
    return null
  }
}

function savePreference(target: LaunchTarget): void {
  try {
    window.localStorage.setItem(PREFERENCE_KEY, target)
  } catch {
    // As above: the choice still applies to this approval, just not the next.
  }
}

/**
 * The editor's own callback URI, if it sent one and it is really its own.
 *
 * <p>Sent by the extension rather than assembled here because only it knows
 * which editor it is running in: VS Code, Insiders, and the other builds each
 * have their own scheme, and a hard-coded `vscode://` sends half of them
 * nowhere.
 *
 * <p>Checked, because this value arrives in a query string. A page that
 * navigates to whatever a URL told it to is an open redirect, and one that
 * accepts any scheme can be pointed at `javascript:`. So: a plausible scheme,
 * never a web or script one, and addressed to this extension.
 */
function safeReturnUri(value: string | null): string | null {
  if (!value) {
    return null
  }
  try {
    const url = new URL(value)
    const scheme = url.protocol.replace(/:$/, '').toLowerCase()
    if (!/^[a-z][a-z0-9.+-]*$/.test(scheme)) {
      return null
    }
    if (['http', 'https', 'javascript', 'data', 'blob', 'file'].includes(scheme)) {
      return null
    }
    return url.host.toLowerCase() === EXTENSION_ID ? url.toString() : null
  } catch {
    return null
  }
}

export function ConnectDevicePage() {
  const [params] = useSearchParams()
  const code = params.get('code') ?? ''

  /**
   * Which app asked, as it said when it opened this page.
   *
   * <p>The only reliable answer: both clients open the same URL and the API
   * records nothing about who asked. A client that says nothing -- the
   * standalone agent, and every copy installed before this existed -- is
   * genuinely unknown, and is asked rather than guessed at.
   */
  const declared = params.get('client')
  const initiator: LaunchTarget | null =
    declared === 'vscode' ? 'vscode' : declared === 'desktop' ? 'desktop' : null
  const returnUri = safeReturnUri(params.get('return'))

  const [pending, setPending] = useState<{
    deviceName: string
    platform: string
    architecture: string
    approved: boolean
  } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [approving, setApproving] = useState(false)
  const [done, setDone] = useState(false)

  /** What is being opened, and whether the choice was ours or theirs. */
  const [target, setTarget] = useState<LaunchTarget | null>(null)
  const [fromPreference, setFromPreference] = useState(false)
  const [launchFailed, setLaunchFailed] = useState(false)
  const [remember, setRemember] = useState(true)
  const fallbackTimer = useRef<number | null>(null)

  const stopWatching = useCallback(() => {
    if (fallbackTimer.current !== null) {
      window.clearTimeout(fallbackTimer.current)
      fallbackTimer.current = null
    }
  }, [])

  /**
   * Hands control to an app, and notices when nothing happens.
   *
   * <p>There is no detecting whether the app is already running from here, and
   * no need to: both schemes are handled by a single instance, so the running
   * copy is focused and a second one is never started. What this has to handle
   * is the other case -- a scheme nothing is registered for, which fails
   * silently and would otherwise leave someone watching a spinner forever.
   */
  const launch = useCallback(
    (to: LaunchTarget) => {
      setTarget(to)
      setLaunchFailed(false)
      stopWatching()

      const href = to === 'vscode' ? (returnUri ?? APPS.vscode.scheme) : APPS.desktop.scheme
      try {
        window.location.href = href
      } catch {
        setLaunchFailed(true)
        return
      }
      fallbackTimer.current = window.setTimeout(() => {
        // Still here, still looking at us: nothing took the focus, so nothing
        // opened. Said plainly, with something to press, rather than left as a
        // spinner that never resolves.
        if (document.hasFocus()) {
          setLaunchFailed(true)
        }
      }, FALLBACK_MS)
    },
    [returnUri, stopWatching],
  )

  // An app that opened took the focus, which is the only evidence available
  // that the launch worked. Stop waiting to call it a failure.
  useEffect(() => {
    const settle = () => {
      if (document.visibilityState === 'hidden' || !document.hasFocus()) {
        stopWatching()
      }
    }
    window.addEventListener('blur', settle)
    document.addEventListener('visibilitychange', settle)
    return () => {
      window.removeEventListener('blur', settle)
      document.removeEventListener('visibilitychange', settle)
      stopWatching()
    }
  }, [stopWatching])

  const load = useCallback(async () => {
    if (!code) {
      setError('This link is missing its pairing code.')
      return
    }
    try {
      const result = await api.pendingPairing(code)
      setPending(result)
      setDone(result.approved)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'This pairing request is no longer valid.')
    }
  }, [code])

  useEffect(() => {
    void load()
  }, [load])

  const approve = async () => {
    setApproving(true)
    try {
      await api.approvePairing(code)
      setDone(true)
      // Hand control back to the app that sent them here. Without this the
      // browser is left in front and the app behind it, with no sign that
      // anything happened.
      //
      // What asked comes first: a saved preference is for the case where
      // nothing said, and overriding a client that did say would open the
      // wrong application in front of someone who had just used the right one.
      if (initiator) {
        launch(initiator)
        return
      }
      const preferred = readPreference()
      if (preferred) {
        setFromPreference(true)
        launch(preferred)
      }
      // Otherwise `target` stays null and the choice is offered below.
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not connect this device.')
    } finally {
      setApproving(false)
    }
  }

  const choose = (to: LaunchTarget) => {
    if (remember) {
      savePreference(to)
    }
    launch(to)
  }

  const forget = () => {
    try {
      window.localStorage.removeItem(PREFERENCE_KEY)
    } catch {
      // Nothing was stored, so nothing needs clearing.
    }
    stopWatching()
    setFromPreference(false)
    setLaunchFailed(false)
    setTarget(null)
  }

  return (
    <div className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center px-6 text-center">
      {error ? (
        <>
          <AlertTriangle size={28} className="mb-3 text-warning" />
          <h1 className="text-lg font-medium">Cannot connect this device</h1>
          <p className="mt-2 text-sm text-content-muted">{error}</p>
          <p className="mt-4 text-xs text-content-muted">
            Run <code className="rounded bg-surface-sunken px-1">nevtan-agent connect</code> again on
            the device to start over.
          </p>
        </>
      ) : done ? (
        <>
          <Check size={28} className="mb-3 text-success" />
          <h1 className="text-lg font-medium">Device connected</h1>
          <p className="mt-2 text-sm text-content-muted">
            {pending?.deviceName} is now paired with your account.
          </p>

          {target === null ? (
            <Chooser remember={remember} onRemember={setRemember} onChoose={choose} />
          ) : launchFailed ? (
            <LaunchFailed app={APPS[target].name} onRetry={() => launch(target)} />
          ) : (
            <Launching
              app={APPS[target].name}
              onChangeApp={fromPreference ? forget : undefined}
            />
          )}
        </>
      ) : !pending ? (
        <Loader2 size={22} className="animate-spin text-content-muted" />
      ) : (
        <>
          <Laptop size={28} className="mb-3 text-content-muted" />
          <h1 className="text-xl font-medium">Connect device</h1>
          <p className="mt-3 text-sm">
            Connect{' '}
            <code className="rounded bg-surface-sunken px-1.5 py-0.5">{pending.deviceName}</code>
          </p>
          <p className="mt-1 text-xs text-content-muted">
            {pending.platform} · {pending.architecture}
          </p>
          <p className="mt-3 text-sm text-content-muted">
            This device will be paired with your account, and will be able to download and run
            models on itself.
          </p>
          <button
            type="button"
            onClick={() => void approve()}
            disabled={approving}
            className="mt-6 w-full rounded-full bg-accent px-6 py-3 text-sm font-medium text-accent-content disabled:opacity-60"
          >
            {approving ? 'Connecting…' : 'Connect'}
          </button>
        </>
      )}
    </div>
  )
}

/**
 * What is happening, while it happens.
 *
 * <p>No button. The app is being opened, and offering to open it again beside
 * a line saying it is already being opened is an invitation to end up with two
 * of them -- which is exactly what the old screen did, and it offered the
 * wrong application at that. Something to press appears only if nothing has
 * happened, which is what {@link LaunchFailed} is for.
 */
function Launching({ app, onChangeApp }: { app: string; onChangeApp?: () => void }) {
  return (
    <>
      <p className="mt-5 flex items-center justify-center gap-2 text-sm">
        <Loader2 size={15} className="animate-spin text-content-muted" />
        Launching {app}…
      </p>
      {onChangeApp ? (
        // A remembered choice nobody can change is a trap, and this is the one
        // screen where changing it is obviously what you would want.
        <button
          type="button"
          onClick={onChangeApp}
          className="mt-3 text-xs text-content-muted underline underline-offset-2"
        >
          Open a different app
        </button>
      ) : null}
    </>
  )
}

/**
 * Nothing said which app asked, and nothing has been remembered.
 *
 * <p>The standalone agent is the honest case for this: it runs on machines
 * with neither application installed, and guessing on its behalf would send
 * someone to an app that is not there.
 */
function Chooser({
  remember,
  onRemember,
  onChoose,
}: {
  remember: boolean
  onRemember: (value: boolean) => void
  onChoose: (target: LaunchTarget) => void
}) {
  return (
    <>
      <p className="mt-5 text-sm">Where would you like to continue?</p>
      <div className="mt-4 flex w-full flex-col gap-2">
        <button
          type="button"
          onClick={() => onChoose('vscode')}
          className="flex items-center justify-center gap-2 rounded-full border border-border px-5 py-2.5 text-sm hover:bg-surface-sunken"
        >
          <Code2 size={16} />
          Open VS Code
        </button>
        <button
          type="button"
          onClick={() => onChoose('desktop')}
          className="flex items-center justify-center gap-2 rounded-full border border-border px-5 py-2.5 text-sm hover:bg-surface-sunken"
        >
          <Sparkles size={16} />
          Open NevTan AI
        </button>
      </div>
      <label className="mt-4 flex items-center justify-center gap-2 text-xs text-content-muted">
        <input
          type="checkbox"
          checked={remember}
          onChange={(event) => onRemember(event.target.checked)}
          className="rounded border-border"
        />
        Remember my choice and don’t ask again
      </label>
    </>
  )
}

/**
 * The app did not open, as far as this page can tell.
 *
 * <p>Hedged deliberately. The signal is that the browser still has focus,
 * which is also what happens when the app opened behind this window or was
 * already running and merely flashed in the taskbar. So it says what is
 * probably true, gives the likeliest innocent explanation, and offers the
 * button rather than asserting a failure it cannot be sure of.
 */
function LaunchFailed({ app, onRetry }: { app: string; onRetry: () => void }) {
  return (
    <>
      <p className="mt-5 text-sm text-content-muted">
        {app} didn’t open automatically. It may already be running — check your taskbar or dock.
      </p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-4 rounded-full border border-border px-5 py-2 text-sm hover:bg-surface-sunken"
      >
        Open {app}
      </button>
      <p className="mt-4 text-xs text-content-muted">
        Your device is connected either way — you can close this tab.
      </p>
    </>
  )
}
